// src/pages/Home.jsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ethers } from 'ethers'
import { useAccount, useConnectorClient } from 'wagmi'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Badge } from '../components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import { Search, TrendingUp, TrendingDown, LogOut, Shield, Edit, Wallet, ShoppingCart, ShieldCheck, AlertCircle, Mail, XCircle, PlusCircle, Trophy, ArrowUp, Copy, Star, Users } from 'lucide-react'
import { formatPR } from '../utils/prCurrency'
import { formatNumber } from '../utils/formatNumber'
import DarkModeToggle from '../components/DarkModeToggle'
import ShareButton from '../components/ShareButton'
import NotificationPanel from '../components/NotificationPanel'
import FeedCard from '../components/FeedCard'
import CategoryFilter from '../components/CategoryFilter'
import DiscussionModal from '../components/DiscussionModal'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function Home() {
  const navigate = useNavigate()
  const { token, logout, address } = useAuth()
  const { connector, isConnected } = useAccount()
  const { data: client } = useConnectorClient()
  const { isDarkMode } = useTheme()
  const [myProfile, setMyProfile] = useState(null)
  const [profiles, setProfiles] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  
  // Feed state
  const [feedItems, setFeedItems] = useState([])
  const [feedPage, setFeedPage] = useState(1)
  const [feedTotalPages, setFeedTotalPages] = useState(1)
  const [isLoadingFeed, setIsLoadingFeed] = useState(true)
  const [isLoadingMoreFeed, setIsLoadingMoreFeed] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [allCategories, setAllCategories] = useState([])
  
  // Discussion modal state
  const [discussionOpen, setDiscussionOpen] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  
  // Profile search pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalProfiles, setTotalProfiles] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  
  // Wallet/PR state
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false)
  const [showBuyDialog, setShowBuyDialog] = useState(false)
  const [buyAmount, setBuyAmount] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  // New States for Wallet Audit
  const [showWalletCheck, setShowWalletCheck] = useState(false)
  const [walletPRBalance, setWalletPRBalance] = useState(0)
  const [isAssetAdded, setIsAssetAdded] = useState(true)
  
  // Scroll to top state
  const [showScrollTop, setShowScrollTop] = useState(false)
  
  // Contract copy state
  const [contractCopied, setContractCopied] = useState(false)

  const CONTRACT = "0xfDbBBcc7dF9aad571f5c206E4Adb3E3aA4e30521"
  const POLYGON_CHAIN_ID = "0x89" // 137 in decimal
  const ABI = [
    "function buy(uint256 humanAmount) payable",
    "function withdrawWithSig(uint256,string,bytes) external",
    "function balanceOf(address) view returns (uint256)",
    "function pricePerTokenWei() view returns (uint256)"
  ]

  // ==== Provider / Signer helper ====
  const getSignerOrThrow = async () => {
    // First try to use wagmi's connector (most reliable for both mobile and desktop)
    if (connector && isConnected) {
      try {
        const provider = await connector.getProvider();
        if (provider) {
          console.log('✓ Using wagmi connector provider:', connector.id);
          const ethersProvider = new ethers.BrowserProvider(provider);
          return ethersProvider.getSigner();
        }
      } catch (e) {
        console.warn('Failed to get provider from wagmi connector:', e);
      }
    }

    // Fallback to manual provider discovery
    let providerObject = null;
    if (typeof window !== 'undefined') {
      if (window.__PR_APP_PROVIDER) {
        providerObject = window.__PR_APP_PROVIDER;
        console.log('✓ Using saved provider from window.__PR_APP_PROVIDER');
      } else if (window.ethereum) {
        providerObject = window.ethereum;
        console.log('✓ Using window.ethereum provider');
      } else if (window.web3 && window.web3.currentProvider) {
        providerObject = window.web3.currentProvider;
        console.log('✓ Using window.web3.currentProvider');
      }
    }

    if (!providerObject) {
      console.error('❌ No provider found. Connector:', connector?.id, 'Connected:', isConnected);
      throw new Error('No web3 provider found. Please reconnect your wallet.');
    }

    // ethers v6: BrowserProvider accepts provider-like objects
    const provider = new ethers.BrowserProvider(providerObject);

    // Ensure accounts are requested (WalletConnect providers often need .request or .enable)
    try {
      if (providerObject.request) {
        await providerObject.request({ method: 'eth_requestAccounts' });
      } else if (providerObject.enable) {
        await providerObject.enable();
      } else if (providerObject.send) {
        await providerObject.send('eth_requestAccounts', []);
      }
    } catch (e) {
      console.error('Account request error:', e);
      throw new Error('Wallet connection request rejected or failed.');
    }

    return provider.getSigner();
  }

  const checkPolygonNetwork = async () => {
    let providerObject = (connector && isConnected) ? await connector.getProvider() : (window.__PR_APP_PROVIDER ?? window.ethereum);
    if (!providerObject) return false;
    try {
      const chainId = await providerObject.request({ method: 'eth_chainId' })
      if (chainId !== POLYGON_CHAIN_ID) {
        try {
          await providerObject.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: POLYGON_CHAIN_ID }] })
          return true
        } catch (switchError) {
          if (switchError.code === 4902) {
            await providerObject.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: POLYGON_CHAIN_ID,
                chainName: 'Polygon Mainnet',
                nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
                rpcUrls: ['https://polygon-rpc.com/'],
                blockExplorerUrls: ['https://polygonscan.com/']
              }]
            })
            return true
          }
          return false
        }
      }
      return true
    } catch (error) { return false }
  }

  const checkWalletPRBalance = async (userAddress) => {
    try {
      let providerObject = (connector && isConnected) ? await connector.getProvider() : (window.__PR_APP_PROVIDER ?? window.ethereum);
      if (!providerObject) return 0;
      const provider = new ethers.BrowserProvider(providerObject);
      const contract = new ethers.Contract(CONTRACT, ABI, provider);
      const balanceBn = await contract.balanceOf(userAddress);
      return Number(ethers.formatUnits(balanceBn, 18));
    } catch (error) { 
      console.error("Balance check error:", error);
      return 0; 
    }
  }

  const handleAddTokenToWallet = async () => {
    try {
      const providerObject = await connector?.getProvider() || window.ethereum;
      const wasAdded = await providerObject.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: CONTRACT,
            symbol: 'PR',
            decimals: 18,
            image: 'https://publicrespect.com/pr-logo.png', 
          },
        },
      });
      if (wasAdded) {
        localStorage.setItem(`pr_asset_added_${address}`, 'true');
        setIsAssetAdded(true);
        toast.success("PR Token added to wallet!");
      }
    } catch (error) { toast.error("Failed to add token to wallet"); }
  }

  const performWalletAudit = async () => {
    if (!address || !isConnected) return;
    try {
      const bal = await checkWalletPRBalance(address);
      setWalletPRBalance(bal);
      const hasAdded = localStorage.getItem(`pr_asset_added_${address}`);
      setIsAssetAdded(!!hasAdded);

      // Show banner only if wallet needs optimization
      if (!hasAdded || bal < 1) {
        setShowWalletCheck(true);
      } else {
        // Auto-close if wallet is already optimized
        setShowWalletCheck(false);
      }
    } catch (e) { console.error("Audit failed", e); }
  }

  useEffect(() => {
    fetchMyProfile()
    fetchCategories()
  }, [address, isConnected])

  // Load user preferences when token is available
  useEffect(() => {
    if (token) {
      loadUserPreferences()
    }
  }, [token])

  // Reload feed when categories change OR when preferences are loaded
  useEffect(() => {
    if (allCategories.length > 0) {
      fetchFeed(1, selectedCategories, false)
    }
  }, [selectedCategories, allCategories])

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`)
      setAllCategories(response.data.categories || [])
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  const loadUserPreferences = async () => {
    if (!token) return
    try {
      const response = await axios.get(`${API}/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const savedCategories = response.data.preferred_categories || []
      if (savedCategories.length > 0) {
        setSelectedCategories(savedCategories)
      }
    } catch (err) {
      console.error('Failed to load preferences:', err)
    }
  }

  const saveUserPreferences = async (categories) => {
    if (!token) return
    try {
      await axios.post(`${API}/preferences`, 
        { preferred_categories: categories },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // Silently saved - no toast needed for every change
    } catch (err) {
      console.error('Failed to save preferences:', err)
      toast.error('Failed to save filter preferences')
    }
  }

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories)
    // Save to backend immediately
    saveUserPreferences(categories)
  }

  const fetchFeed = async (page = 1, categories = selectedCategories, append = false) => {
    if (append) {
      setIsLoadingMoreFeed(true)
    } else {
      setIsLoadingFeed(true)
    }
    
    try {
      const categoryParam = categories.length > 0 ? `&categories=${categories.join(',')}` : ''
      const response = await axios.get(
        `${API}/feed/public?page=${page}&limit=50${categoryParam}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      )
      
      if (append) {
        setFeedItems(prev => [...prev, ...response.data.transactions])
      } else {
        setFeedItems(response.data.transactions || [])
      }
      
      setFeedPage(response.data.page)
      setFeedTotalPages(response.data.pages)
    } catch (err) {
      console.error('Failed to load feed:', err)
      toast.error('Failed to load feed')
    } finally {
      setIsLoadingFeed(false)
      setIsLoadingMoreFeed(false)
    }
  }

  const loadMoreFeed = () => {
    if (feedPage < feedTotalPages) {
      fetchFeed(feedPage + 1, selectedCategories, true)
    }
  }

  const handleVote = async (transactionId, voteType) => {
    if (!token) {
      toast.error('Please connect your wallet to vote')
      navigate('/connect')
      return
    }

    try {
      await axios.post(
        `${API}/respect/vote/${transactionId}`,
        { vote: voteType },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { vote: voteType }
        }
      )
      toast.success(`Vote recorded! Transaction will process after 10 ${voteType === 'approve' ? 'approvals' : 'rejections'}.`)
      fetchFeed(feedPage, selectedCategories, false)
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${voteType} transaction`)
    }
  }

  const openDiscussion = (transactionId) => {
    setSelectedTransactionId(transactionId)
    setDiscussionOpen(true)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setIsSearching(false)
    setProfiles([])
  }

  const fetchMyProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setMyProfile(response.data)
    } catch (err) {
      console.error('Failed to fetch profile:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      clearSearch()
      return
    }
    
    setIsSearching(true)
    setIsLoading(true)
    setCurrentPage(1)
    try {
      const response = await axios.get(`${API}/profiles?q=${searchQuery}&page=1&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setProfiles(response.data.profiles)
      setCurrentPage(response.data.page)
      setTotalPages(response.data.pages)
      setTotalProfiles(response.data.total)
    } catch (err) {
      toast.error('Search failed')
    } finally {
      setIsLoading(false)
    }
  }

  const loadMoreProfiles = () => {
    if (currentPage < totalPages && isSearching) {
      fetchProfilesPage(currentPage + 1, true)
    }
  }

  const fetchProfilesPage = async (page = 1, append = false) => {
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
    }
    
    try {
      const response = await axios.get(`${API}/profiles?q=${searchQuery}&page=${page}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      
      if (append) {
        setProfiles(prev => [...prev, ...response.data.profiles])
      } else {
        setProfiles(response.data.profiles)
      }
      
      setCurrentPage(response.data.page)
      setTotalPages(response.data.pages)
      setTotalProfiles(response.data.total)
    } catch (err) {
      toast.error('Failed to fetch profiles')
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/connect')
  }

  const handleWithdraw = async () => {
    // parse raw user input (string)
    const raw = withdrawAmount?.toString().trim();
    const parsed = Number(raw);

    if (!raw || Number.isNaN(parsed)) {
      toast.error('Please enter a valid numeric amount');
      return;
    }
    if (parsed < 1) {
      toast.error('Minimum withdrawal amount is 1 PR');
      return;
    }

    // Work in integer PR units only
    const humanAmountInt = Math.floor(parsed);
    if (humanAmountInt !== parsed) {
      toast.info(`Rounding down to ${humanAmountInt} PR (fractions not supported)`);
    }

    setIsProcessing(true);
    try {
      const isPolygon = await checkPolygonNetwork();
      if (!isPolygon) { setIsProcessing(false); return; }

      // Get signer (handles WalletConnect and MetaMask)
      const signer = await getSignerOrThrow();
      const userAddress = await signer.getAddress();

      // Check wallet PR balance (must have at least 1 PR token in wallet)
      const walletPRBalance = await checkWalletPRBalance(userAddress);
      if (walletPRBalance < 1) {
        toast.error('You need at least 1 PR in your wallet to initiate withdrawal. Please buy PR first.');
        setShowWithdrawDialog(false);
        setShowBuyDialog(true);
        setIsProcessing(false);
        return;
      }

      // Check profile verification status
      const verificationStatus = myProfile?.verification_status || 'unverified';
      if (verificationStatus === 'unverified') {
        toast.error('Profile verification required for withdrawals. Redirecting to Edit Profile...');
        setShowWithdrawDialog(false);
        setIsProcessing(false);
        setTimeout(() => navigate('/edit-profile'), 1500);
        return;
      }
      if (verificationStatus === 'pending_review') {
        toast.info('Your profile verification is pending manual review. Withdrawals will be enabled once approved.');
        setShowWithdrawDialog(false);
        setIsProcessing(false);
        return;
      }

      // Check in-app withdrawable balance
      const withdrawableBalance = myProfile?.withdrawable_balance || 0;
      if (withdrawableBalance < humanAmountInt) {
        toast.error(`Insufficient app balance. You have ${withdrawableBalance.toFixed(4)} PR available`);
        setIsProcessing(false);
        return;
      }

      // Step 1: Request backend signature (send integer string)
      toast.info('Requesting withdrawal signature...');
      const signResponse = await axios.post(
        `${API}/get-withdraw-sign`,
        { user: userAddress, amount: String(humanAmountInt) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { nonce, signature } = signResponse.data;
      if (!nonce || !signature) throw new Error('Invalid signature response from backend');

      // Step 2: Call smart contract withdrawWithSig
      toast.info('Submitting transaction to Polygon...');
      const contract = new ethers.Contract(CONTRACT, ABI, signer);

      // signature is hex string -> pass directly as bytes (ethers handles it)
      const tx = await contract.withdrawWithSig(humanAmountInt, nonce, signature);
      if (tx && tx.hash) toast.info(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      const receipt = await tx.wait();
      const receiptHash = receipt?.transactionHash || receipt?.hash;
      if (receiptHash) toast.success(`Withdrawal successful! Hash: ${receiptHash.slice(0, 10)}...`);
      else toast.success('Withdrawal successful! Transaction mined.');

      // Step 4: Update backend balance after success
      await axios.post(
        `${API}/withdraw`,
        { amount: humanAmountInt },
        { headers: { Authorization: `Bearer ${token}` }, params: { amount: humanAmountInt } }
      );

      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchMyProfile();

    } catch (err) {
      console.error('Withdrawal error:', err);
      if (err.response?.data?.detail) toast.error(err.response.data.detail);
      else if (err.response?.data?.error) toast.error(err.response?.data?.error);
      else if (err.message) toast.error(err.message);
      else toast.error('Withdrawal failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }

  const handleBuyPR = async () => {
    const amount = parseInt(buyAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    try {
      const isPolygon = await checkPolygonNetwork();
      if (!isPolygon) { setIsProcessing(false); return; }

      const signer = await getSignerOrThrow();
      const contract = new ethers.Contract(CONTRACT, ABI, signer);

      // Read pricePerTokenWei directly from contract
      const priceBn = await contract.pricePerTokenWei(); // BigNumber
      const priceBigInt = BigInt(priceBn.toString()); // wei per 1 PR
      const totalCost = priceBigInt * BigInt(amount);

      toast.info('Submitting purchase to Polygon...');
      const tx = await contract.buy(amount, { value: totalCost });
      if (tx && tx.hash) toast.info(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      toast.success(`Successfully bought ${amount} PR!`);
      setShowBuyDialog(false);
      setBuyAmount('');
    } catch (err) {
      console.error('Buy PR error:', err);
      toast.error(err.response?.data?.error || err.message || 'Purchase failed');
    } finally {
      setIsProcessing(false);
    }
  }

  const getInitials = (name) => {
    if (!name) return ''
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Defensive wallet display
  const walletShort = address ? `${address.slice(0,6)}...${address.slice(-4)}` : 'Not connected'

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm border-b sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/pr-logo.png" alt="Public Respect Logo" className="w-10 h-10" />
              <h1 className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-indigo-400 to-purple-400' : 'from-indigo-600 to-purple-600'} bg-clip-text text-transparent`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Public Respect
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <NotificationPanel token={token} address={address} />
              {myProfile && (
                <ShareButton 
                  url={`${window.location.origin}/profile/${myProfile.username}`}
                  title={`Check out ${myProfile.name}'s profile on Public Respect`}
                  variant="outline"
                  size="sm"
                />
              )}
              <Button
                type="button"
                onClick={() => navigate('/contact-us')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                title="Contact Us"
              >
                <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Contact</span>
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/leaderboard')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                title="Leaderboard"
              >
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Leaderboard</span>
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/my-scouted-profiles')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                title="My Scouted Profiles"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Scouted</span>
              </Button>
              <DarkModeToggle />
              <Button
                type="button"
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Banned User Warning */}
      {myProfile && myProfile.is_banned && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-600 border-4 border-red-800 rounded-lg p-6 mb-4 shadow-xl">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-12 h-12 text-white flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">⚠️ YOUR PROFILE IS RED FLAGGED</h2>
                <p className="text-white text-lg mb-2">
                  You are in <strong>Read Only Mode</strong>. All actions are disabled.
                </p>
                <p className="text-white">
                  Please contact us at <a href="mailto:info@publicrespect.com" className="underline font-bold">info@publicrespect.com</a> to resolve this issue.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* My Profile Card */}
        {myProfile && (
          <Card data-testid="my-profile-card" className={`mb-8 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
            <CardHeader>
              <CardTitle className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${isDarkMode ? 'text-gray-100' : ''}`}>
                <span style={{fontFamily: 'Space Grotesk, sans-serif'}}>My Profile</span>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Badge
                    data-testid="my-respect-points"
                    className={`text-sm sm:text-lg px-3 py-1 sm:px-4 ${
                      myProfile.respect_points >= 100
                        ? 'bg-green-500'
                        : myProfile.respect_points >= 0
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  >
                    {myProfile.respect_points >= 0 ? '+' : ''}{formatNumber(myProfile.respect_points)} Respect
                  </Badge>

                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 sm:px-4 rounded-full text-xs sm:text-sm font-semibold">
                    <img src="/pr-coin.png" alt="PR" className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>{formatPR(myProfile.respect_points)} PR</span>
                  </div>

                  {/* Withdraw Trigger (mobile-safe button) */}
                  <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center gap-1.5 bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 py-1 sm:px-4 rounded-full text-xs sm:text-sm font-semibold ${myProfile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-green-500 hover:to-emerald-600'}`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        onClick={() => !myProfile.is_banned && console.log('Withdraw trigger clicked (UI)')}
                        onTouchStart={() => {}}
                        disabled={myProfile.is_banned}
                      >
                        <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>{(myProfile.withdrawable_balance || 0).toFixed(4)} PR</span>
                      </button>
                    </DialogTrigger>

                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Withdraw PR</DialogTitle>
                        <DialogDescription>
                          Available balance: {(myProfile.withdrawable_balance || 0).toFixed(4)} PR<br/>
                          <span className="text-xs text-orange-600">⚠️ Requires: At least 1 PR in your wallet + Polygon Mainnet</span>
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="withdraw-amount">Amount to Withdraw (minimum 1 PR, integers only)</Label>
                          <Input
                            id="withdraw-amount"
                            type="number"
                            min="1"
                            step="1"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            placeholder="Enter amount (e.g., 10)"
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">Gas fee paid in POL. Fractional amounts will be rounded down.</p>
                        </div>
                        <Button
                          type="button"
                          onClick={() => { console.log('Withdraw button pressed'); handleWithdraw(); }}
                          disabled={isProcessing}
                          className="w-full bg-green-500 hover:bg-green-600"
                        >
                          {isProcessing ? 'Processing...' : 'Withdraw to Wallet'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Buy Trigger (mobile-safe button) */}
                  <Dialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1.5 rounded-md text-white text-sm font-semibold ${myProfile.is_banned ? 'opacity-50 cursor-not-allowed' : 'hover:from-purple-600 hover:to-pink-600'}`}
                        onClick={() => !myProfile.is_banned && console.log('Buy trigger clicked (UI)')}
                        onTouchStart={() => {}}
                        disabled={myProfile.is_banned}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Buy PR</span>
                      </button>
                    </DialogTrigger>

                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Buy PR</DialogTitle>
                        <DialogDescription>
                          Price: 5 POL per PR token
                          <div>Note: Make sure you have enough - POL(Polygon Ecosystem Token)</div>
                        </DialogDescription>

                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="buy-amount">Amount to Buy</Label>
                          <Input
                            id="buy-amount"
                            type="number"
                            value={buyAmount}
                            onChange={(e) => setBuyAmount(e.target.value)}
                            placeholder="Enter amount"
                            className="mt-1"
                          />
                        </div>
                        <div className="text-sm text-gray-600">
                          Total cost: {buyAmount ? (parseInt(buyAmount) * 5).toFixed(2) : '0'} POL
                        </div>
                        <Button
                          type="button"
                          onClick={handleBuyPR}
                          disabled={isProcessing}
                          className="w-full bg-purple-500 hover:bg-purple-600"
                        >
                          {isProcessing ? 'Processing...' : 'Buy Now'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Avatar className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-purple-200 mx-auto sm:mx-0">
                  {myProfile.profile_image ? (
                    <AvatarImage src={myProfile.profile_image} alt={myProfile.name} />
                  ) : (
                    <AvatarFallback className="text-xl sm:text-2xl font-bold bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                      {getInitials(myProfile.name)}
                    </AvatarFallback>
                  )}
                </Avatar>

                <div className="flex-1 w-full text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <h2 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>{myProfile.name}</h2>
                    {myProfile.verification_status === 'verified' ? (
                      <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" title="Verified Profile" />
                    ) : (
                      <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" title="Unverified Profile" />
                    )}
                  </div>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>@{myProfile.username}</p>
                  {myProfile.bio && <p className={`mt-2 text-sm sm:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{myProfile.bio}</p>}
                  <div className={`mt-3 flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>Wallet: {walletShort}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button
                    type="button"
                    data-testid="edit-profile-button"
                    onClick={() => !myProfile.is_banned && navigate('/edit-profile')}
                    variant="outline"
                    className={`flex items-center gap-2 w-full sm:w-auto text-sm ${myProfile.is_banned ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={myProfile.is_banned}
                  >
                    <Edit className="w-4 h-4" />
                    Edit Profile
                  </Button>

                  <Button
                    type="button"
                    data-testid="view-my-profile-button"
                    onClick={() => navigate(`/profile/${myProfile.username}`)}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 w-full sm:w-auto text-sm"
                  >
                    View Full Profile
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search profiles by username or name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  if (!e.target.value.trim()) {
                    clearSearch()
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className={`pl-10 h-12 backdrop-blur-sm ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white/90'}`}
              />
            </div>
            <Button type="button" data-testid="search-button" onClick={handleSearch} className="h-12 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700">
              Search
            </Button>
            {isSearching && (
              <Button type="button" variant="outline" onClick={clearSearch} className="h-12">
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Category Filter - Only show when not searching */}
        {!isSearching && (
          <CategoryFilter
            categories={allCategories}
            selected={selectedCategories}
            onChange={handleCategoryChange}
          />
        )}

        {/* Content: Either Profile Search Results or Public Feed */}
        {isSearching ? (
          /* Profile Search Results */
          <div className="mt-6">
            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Search Results
            </h2>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <>
                {/* Show Create Claimable Profile button when results < 3 */}
                {profiles.length < 3 && (
                  <Card className={`mb-6 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border-2 border-dashed`}>
                    <CardContent className="py-6">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3 text-center sm:text-left">
                          <div className={`p-3 rounded-full ${isDarkMode ? 'bg-yellow-800/50' : 'bg-yellow-100'}`}>
                            <Star className={`w-6 h-6 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                          </div>
                          <div>
                            <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                              Can't find who you're looking for?
                            </h3>
                            <p className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                              Create a claimable profile — Earn <strong>10% of the respects</strong> they receive once claimed!
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => navigate('/scout-profile')}
                          className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white whitespace-nowrap"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Create Claimable Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {profiles.length === 0 ? (
                  <Card className={`backdrop-blur-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90'}`}>
                    <CardContent className={`py-12 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No profiles found for "{searchQuery}"
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div data-testid="profiles-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {profiles.map((profile) => (
                        <Card
                          key={profile.wallet_address || profile.username}
                          data-testid={`profile-card-${profile.username}`}
                          className={`backdrop-blur-sm hover:shadow-xl cursor-pointer transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-purple-500' : 'bg-white/90 border-purple-100'}`}
                          onClick={() => navigate(`/profile/${profile.username}`)}
                        >
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                              <Avatar className={`w-14 h-14 border-2 ${isDarkMode ? 'border-purple-400' : 'border-purple-200'}`}>
                                {profile.profile_image ? (
                                  <AvatarImage src={profile.profile_image} alt={profile.name} />
                                ) : (
                                  <AvatarFallback className="font-bold bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                                    {getInitials(profile.name)}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className={`font-bold text-lg truncate ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>{profile.name}</h3>
                                  {/* Profile status badges - UNCLAIMED only hides when VERIFIED */}
                                  {profile.is_claimable && profile.verification_status !== 'verified' ? (
                                    profile.is_claimed ? (
                                      <div className="flex items-center gap-1 bg-orange-500 px-2 py-0.5 rounded">
                                        <AlertCircle className="w-3 h-3 text-white flex-shrink-0" />
                                        <span className="text-xs text-white font-bold">CLAIMED</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 bg-yellow-500 px-2 py-0.5 rounded">
                                        <AlertCircle className="w-3 h-3 text-white flex-shrink-0" />
                                        <span className="text-xs text-white font-bold">UNCLAIMED</span>
                                      </div>
                                    )
                                  ) : profile.is_banned ? (
                                    <div className="flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded">
                                      <AlertCircle className="w-4 h-4 text-white flex-shrink-0" />
                                      <span className="text-xs text-white font-bold">RED FLAGGED</span>
                                    </div>
                                  ) : profile.verification_status === 'verified' ? (
                                    <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" title="Verified" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" title="Unverified" />
                                  )}
                                </div>
                                <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{profile.username}</p>
                                {profile.bio && (
                                  <p className={`mt-2 text-sm line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{profile.bio}</p>
                                )}
                                <div className="mt-3">
                                  <Badge
                                    data-testid={`respect-badge-${profile.username}`}
                                    className={`${
                                      profile.respect_points >= 100
                                        ? 'bg-green-500'
                                        : profile.respect_points >= 0
                                        ? 'bg-yellow-500'
                                        : 'bg-red-500'
                                    }`}
                                  >
                                {profile.respect_points >= 0 ? (
                                  <><TrendingUp className="w-3 h-3 mr-1" /> {formatNumber(profile.respect_points)}</>
                                ) : (
                                  <><TrendingDown className="w-3 h-3 mr-1" /> {formatNumber(profile.respect_points)}</>
                                )}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {/* Load More Button for Profiles */}
                {profiles.length > 0 && currentPage < totalPages && (
                  <div className="mt-8 text-center">
                    <Button
                      onClick={loadMoreProfiles}
                      disabled={isLoadingMore}
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                    >
                      {isLoadingMore ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        `Load More (${profiles.length} of ${totalProfiles})`
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    ) : (
          /* Public Feed */
          <div className="mt-6">
            {/* How It Works Section */}
            <Card className={`mb-6 p-4 ${isDarkMode ? 'bg-indigo-900/30 border-indigo-700' : 'bg-indigo-50 border-indigo-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${isDarkMode ? 'bg-indigo-800' : 'bg-indigo-100'}`}>
                  <Shield className={`w-5 h-5 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>
                    How it works
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    Each transaction needs <strong>10 community votes</strong> (approve or reject). 
                    Voters earn <strong>1% reward</strong> each. Anyone can view, but only connected wallets can vote.
                  </p>
                </div>
              </div>
            </Card>
            
            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Public Feed
            </h2>
            {isLoadingFeed ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : feedItems.length === 0 ? (
              <Card className={`backdrop-blur-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90'}`}>
                <CardContent className={`py-12 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="mb-2">No transactions found</p>
                  {selectedCategories.length > 0 && (
                    <p className="text-sm">Try removing some category filters</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="space-y-4">
                  {feedItems.map(transaction => (
                    <FeedCard
                      key={transaction.id}
                      transaction={transaction}
                      currentWallet={address}
                      onVote={handleVote}
                      onDiscuss={openDiscussion}
                    />
                  ))}
                </div>
                
                {/* Load More Button for Feed */}
                {feedPage < feedTotalPages && (
                  <div className="mt-8 text-center">
                    <Button
                      onClick={loadMoreFeed}
                      disabled={isLoadingMoreFeed}
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                    >
                      {isLoadingMoreFeed ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Discussion Modal */}
        <DiscussionModal
          open={discussionOpen}
          transactionId={selectedTransactionId}
          onClose={() => setDiscussionOpen(false)}
        />
        
        {/* Scroll to Top Button */}
        {showScrollTop && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 rounded-full w-12 h-12 p-0 bg-indigo-600 hover:bg-indigo-700 shadow-lg"
            title="Scroll to top"
          >
            <ArrowUp className="w-6 h-6" />
          </Button>
        )}
      </div>
    </div>
  )
}